import React from "react";
import { Link } from "react-router-dom";
import { CreditCard, Phone, Mail, Send } from "lucide-react";
import PaymentQrCode from "./PaymentQrCode";
import { ReadableCard, ReadableNavButton } from "./Sticker";
import LinkifiedText from "./LinkifiedText";
import { pricingDueParts } from "../lib/format";
import { combinedPaymentValue, hasPaymentMethod, looksLikeUpiId } from "../lib/payment";

function isPaymentUrl(value) {
  return /^https?:\/\//i.test((value || "").trim());
}

function detailTestId(idPrefix, name, variant) {
  if (variant === "guest") {
    const legacy = {
      "link-btn": "payment-link-btn",
      upi: "payment-upi",
      id: "payment-id",
      instructions: "payment-instructions",
      phone: "contact-phone",
      email: "contact-email",
      telegram: "contact-telegram",
    };
    if (legacy[name]) return legacy[name];
  }
  return `${idPrefix}-${name}`;
}

export function SelfPaymentDetails({
  selfPayment,
  pricing,
  payeeName,
  idPrefix = "payment",
  variant = "guest",
  qrSize = 160,
  compact = false,
}) {
  const paymentValue = combinedPaymentValue(selfPayment);
  const linkIsUrl = isPaymentUrl(paymentValue);
  const instructions = (selfPayment.instructions || "").trim();
  const phone = (selfPayment.contact_phone || "").trim();
  const email = (selfPayment.contact_email || "").trim();
  const telegram = (selfPayment.contact_telegram || "").trim();
  const telegramUrl = telegram
    ? telegram.startsWith("http")
      ? telegram
      : `https://t.me/${telegram.replace(/^@/, "")}`
    : null;

  return (
    <div className={`flex items-start gap-4 ${compact ? "flex-col" : "flex-col sm:flex-row gap-5"}`}>
      {hasPaymentMethod(selfPayment) ? (
        <PaymentQrCode
          selfPayment={selfPayment}
          pricing={pricing}
          payeeName={payeeName}
          size={compact ? Math.min(qrSize, 140) : qrSize}
        />
      ) : null}
      <div className="flex-1 space-y-3 min-w-0 w-full">
        {paymentValue && linkIsUrl && (
          <a
            href={paymentValue}
            target="_blank"
            rel="noreferrer"
            className="rounded-md2 border border-black/[0.08] bg-white px-4 py-2 inline-flex font-ui font-semibold text-[14px] hover:bg-cream"
            data-testid={detailTestId(idPrefix, "link-btn", variant)}
          >
            Open payment link →
          </a>
        )}
        {paymentValue && !linkIsUrl && (
          <p
            className="font-mono text-[14px] font-semibold rounded-md2 border border-black/[0.08] bg-cream px-3 py-2 inline-block"
            data-testid={detailTestId(idPrefix, looksLikeUpiId(paymentValue) ? "upi" : "id", variant)}
          >
            {looksLikeUpiId(paymentValue) ? "UPI" : "Payment ID"}: {paymentValue}
          </p>
        )}
        {instructions && (
          <div className="pt-3 border-t border-black/[0.06]">
            <p className="font-ui text-[11px] font-medium text-ink/70 mb-1">How to pay</p>
            <p
              className="font-semibold text-[14px] leading-relaxed whitespace-pre-line break-words"
              data-testid={detailTestId(idPrefix, "instructions", variant)}
            >
              <LinkifiedText>{instructions}</LinkifiedText>
            </p>
          </div>
        )}
        {(phone || email || telegram) && (
          <div className="pt-3 border-t border-black/[0.06]">
            <p className="font-ui text-[11px] font-medium text-ink/70 mb-2">
              {variant === "guest" ? "Get in touch with the host" : "Get in touch"}
            </p>
            <div className="flex flex-wrap gap-2">
              {phone && (
                <ReadableNavButton
                  as="a"
                  href={`tel:${phone}`}
                  className="rounded-pill px-3 py-1.5 text-[13px]"
                  data-testid={detailTestId(idPrefix, "phone", variant)}
                >
                  <Phone className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden /> {phone}
                </ReadableNavButton>
              )}
              {email && (
                <ReadableNavButton
                  as="a"
                  href={`mailto:${email}`}
                  className="rounded-pill px-3 py-1.5 text-[13px]"
                  data-testid={detailTestId(idPrefix, "email", variant)}
                >
                  <Mail className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden /> {email}
                </ReadableNavButton>
              )}
              {telegramUrl && (
                <ReadableNavButton
                  as="a"
                  href={telegramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-pill px-3 py-1.5 text-[13px]"
                  data-testid={detailTestId(idPrefix, "telegram", variant)}
                >
                  <Send className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden />{" "}
                  {telegram.replace(/^https?:\/\/t\.me\//, "@")}
                </ReadableNavButton>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Structured payment details — guest event page or profile-default preview on create event.
 */
export default function SelfPaymentDisplay({
  selfPayment,
  pricing,
  payeeName,
  organizerName,
  variant = "guest",
  idPrefix = "payment",
  onHostProfileClick,
}) {
  const resolvedPayeeName = payeeName ?? organizerName;
  const due = pricingDueParts(pricing);
  if (variant === "profile") {
    if (!hasPaymentMethod(selfPayment)) {
      return (
        <div
          className="rounded-md2 border border-black/[0.08] bg-cream/60 px-4 py-4 space-y-2"
          data-testid={`${idPrefix}-profile-empty`}
        >
          <p className="font-ui font-medium text-[14px] text-ink">No payment details on your profile yet.</p>
          <p className="font-ui text-[13px] text-ink/75 leading-relaxed">
            Add them in your{" "}
            {typeof onHostProfileClick === "function" ? (
              <button
                type="button"
                onClick={onHostProfileClick}
                className="font-semibold text-plum underline underline-offset-2"
                data-testid={`${idPrefix}-host-profile-link`}
              >
                host profile
              </button>
            ) : (
              <Link
                to="/me?tab=host&section=payment"
                className="font-semibold text-plum underline underline-offset-2"
                data-testid={`${idPrefix}-host-profile-link`}
              >
                host profile
              </Link>
            )}
            , or uncheck above to set payment for this event only.
          </p>
        </div>
      );
    }

    return (
      <div
        className="rounded-md2 border border-black/[0.08] bg-cream/40 px-4 py-4 space-y-3"
        data-testid={`${idPrefix}-profile-summary`}
      >
        <p className="font-ui text-[12px] font-medium text-ink/70">From your profile</p>
        <SelfPaymentDetails
          selfPayment={selfPayment}
          pricing={pricing}
          payeeName={resolvedPayeeName}
          idPrefix={idPrefix}
          variant="profile"
        />
      </div>
    );
  }

  return (
    <ReadableCard className="p-5 sm:p-6 space-y-4">
      <div className="flex items-start gap-3">
        <span className="rounded-md2 border border-black/[0.08] bg-cream p-2 shrink-0">
          <CreditCard className="w-5 h-5" strokeWidth={2.5} aria-hidden />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-ui text-[12px] font-medium text-ink/70">
            Payment · {due.label}
          </p>
          <p className="font-ui font-semibold text-[17px] sm:text-[18px] leading-tight mt-0.5">
            Pay the host directly, then confirm with them.
          </p>
          {due.note && (
            <p className="font-ui text-[12px] text-ink/70 mt-1">{due.note}</p>
          )}
        </div>
      </div>

      <SelfPaymentDetails
        selfPayment={selfPayment}
        pricing={pricing}
        payeeName={resolvedPayeeName}
        idPrefix={idPrefix}
        variant="guest"
        qrSize={180}
      />
    </ReadableCard>
  );
}
